import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, RefreshCw } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/assignments";

const KINDS = {
  appointments: { url: "/assignments/backfill/orphan-appointments", idKey: "appointment_id", title: "Appuntamenti senza pratica in Coda",
    desc: "Ultimi 90 giorni, esclusi gli Annullati. Seleziona gli appuntamenti da recuperare: viene creata solo la pratica mancante, clienti, opportunità e appuntamenti non vengono modificati.",
    bodyKey: "appointment_ids", params: { confirm: true, scope: "selezione" }, empty: "Nessun appuntamento senza pratica. Tutto allineato.",
    confirm: (n) => `Verranno riallineati ${n} appuntamenti selezionati: una pratica per cliente, intestata a chi ha fissato l'appuntamento; più appuntamenti dello stesso cliente confluiscono nella stessa pratica. L'operazione è registrata nell'audit e non modifica clienti, opportunità o appuntamenti.` },
  opportunities: { url: "/assignments/backfill/orphan-opportunities", idKey: "opportunity_id", title: "Lead (opportunità) senza pratica in Coda",
    desc: "Ultimi 12 mesi, opportunità aperte senza alcuna pratica in Coda Lavori (tipicamente lead inseriti prima dell'autocreazione delle pratiche). Viene creata solo la pratica mancante, intestata a chi ha inserito il lead.",
    bodyKey: "opportunity_ids", params: { confirm: true }, empty: "Nessun lead senza pratica. Tutto allineato.",
    confirm: (n) => `Verranno create le pratiche mancanti per ${n} lead selezionati, intestate a chi li ha inseriti (operatore → «Da prendere in carico»; Direzione/Back Office → «Preso in carico»). Nessun cliente od opportunità viene modificato.` },
};

export default function BackfillDialog({ open, onOpenChange, onDone, kind = "appointments" }) {
  const K = KINDS[kind];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(K.url); setData(data); setSelected(new Set()); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectCat = (cat) => setSelected(new Set((data?.items || []).filter((i) => !cat || i.category === cat).map((i) => i[K.idKey])));

  const run = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(K.url, { [K.bodyKey]: [...selected] }, { params: K.params });
      toast.success(`${r.created} pratiche create${r.linked ? ` · ${r.linked} appuntamenti agganciati a pratiche esistenti` : ""}`);
      setConfirm(false);
      await load();
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore riallineamento"); }
    finally { setBusy(false); }
  };

  const n = selected.size;
  const allChecked = data?.items?.length > 0 && n === data.items.length;
  const isAppt = kind === "appointments";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="backfill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RefreshCw className="h-5 w-5 text-brand" /> {K.title}</DialogTitle>
          <DialogDescription>{K.desc}</DialogDescription>
        </DialogHeader>
        {loading || !data ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Totale" value={data.total} testid="backfill-total" />
              {isAppt && <Stat label="Commerciali" value={data.commerciali} testid="backfill-commerciali" tone="text-brand" />}
              {isAppt && <Stat label="Altri (sportello, telefonata…)" value={data.altri} testid="backfill-altri" tone="text-slate-500" />}
            </div>
            {data.total === 0 ? (
              <div className="rounded-md bg-emerald-50 px-3 py-3 text-sm text-emerald-700" data-testid="backfill-empty">{K.empty}</div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-slate-500">Selezione rapida:</span>
                  {isAppt && <Button variant="outline" size="sm" onClick={() => selectCat("commerciale")} data-testid="backfill-select-commerciali">Commerciali ({data.commerciali})</Button>}
                  <Button variant="outline" size="sm" onClick={() => selectCat(null)} data-testid="backfill-select-tutti">Tutti ({data.total})</Button>
                  <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} disabled={!n} data-testid="backfill-select-none">Nessuno</Button>
                  <span className="ml-auto font-medium text-slate-700" data-testid="backfill-selected-count">{n} selezionati</span>
                </div>
                <div className="max-h-[40vh] overflow-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 text-left text-slate-500">
                      <tr>
                        <th className="px-2 py-1.5"><input type="checkbox" checked={allChecked} onChange={() => (allChecked ? setSelected(new Set()) : selectCat(null))} data-testid="backfill-check-all" aria-label="Seleziona tutti" /></th>
                        <th className="px-2 py-1.5">Data</th><th className="px-2 py-1.5">Cliente</th><th className="px-2 py-1.5">Sede</th><th className="px-2 py-1.5">Tipo</th><th className="px-2 py-1.5">Creato da</th><th className="px-2 py-1.5">{isAppt ? "Categoria" : "Stato"}</th>
                      </tr>
                    </thead>
                    <tbody data-testid="backfill-list">
                      {data.items.map((i) => { const id = i[K.idKey]; return (
                        <tr key={id} className={`cursor-pointer border-t border-slate-100 ${selected.has(id) ? "bg-brand/5" : ""}`} onClick={() => toggle(id)} data-testid={`backfill-row-${id}`}>
                          <td className="px-2 py-1.5"><input type="checkbox" checked={selected.has(id)} onChange={() => toggle(id)} onClick={(e) => e.stopPropagation()} data-testid={`backfill-check-${id}`} aria-label={`Seleziona ${i.customer_name || ""}`} /></td>
                          <td className="whitespace-nowrap px-2 py-1.5">{isAppt ? `${fmtDate(i.appt_date)}${i.appt_time ? ` ${i.appt_time}` : ""}` : fmtDateTime(i.created_at)}</td>
                          <td className="px-2 py-1.5 font-medium text-slate-700">{i.customer_name || "—"}</td>
                          <td className="px-2 py-1.5">{i.office_name || "—"}</td>
                          <td className="px-2 py-1.5">{isAppt ? `${i.type || "—"}${i.has_opportunity ? " · opp." : ""}` : (i.product || "—")}</td>
                          <td className="px-2 py-1.5">{i.created_by || "—"}{i.created_by_role ? ` (${i.created_by_role})` : ""}</td>
                          <td className="px-2 py-1.5">{isAppt
                            ? <span className={`rounded px-1.5 py-0.5 font-medium ${i.category === "commerciale" ? "bg-brand/10 text-brand" : "bg-slate-100 text-slate-500"}`}>{i.category}</span>
                            : <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">{i.stage}</span>}</td>
                        </tr>
                      ); })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="backfill-close">Chiudi</Button>
          {data?.total > 0 && (
            <Button onClick={() => setConfirm(true)} disabled={busy || !n} data-testid="backfill-run">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Riallinea ${n} selezionati`}
            </Button>
          )}
        </DialogFooter>
        <AlertDialog open={confirm} onOpenChange={setConfirm}>
          <AlertDialogContent data-testid="backfill-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Confermi il riallineamento?</AlertDialogTitle>
              <AlertDialogDescription>{K.confirm(n)}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="backfill-confirm-cancel">Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={run} data-testid="backfill-confirm-ok">Procedi</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testid, tone = "text-slate-800" }) {
  return (
    <div className="rounded-md border border-border px-2 py-2">
      <div className={`text-2xl font-semibold ${tone}`} data-testid={testid}>{value}</div>
      <div className="text-[11px] text-slate-500">{label}</div>
    </div>
  );
}
